import type { ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import type { CampaignAnswer } from "@/shared/types/featureTeasing.types";
import { Button } from "@/shared/ui/Button";

type Props = {
  revealed: boolean
  disabled: boolean
  canSelfServe: boolean
  onRespond: (answer: CampaignAnswer) => void
};

// Both positive answers carry the same weight: the CSE chooses, we do not steer.
// Skewed "offset shadow" style; violet-300 shadow reads on the dark overlay and on light/dark cards.
const PRIMARY = "group h-auto min-h-12 w-full min-w-0 -skew-x-12 rounded-none border-0 bg-violet-600 px-8 text-base font-semibold text-white shadow-[6px_6px_0_var(--color-violet-300)] transition-[translate,box-shadow] duration-150 ease-out hover:-translate-x-0.5 hover:-translate-y-0.5 hover:bg-violet-600 hover:text-white hover:shadow-[8px_8px_0_var(--color-violet-300)] active:translate-x-1 active:translate-y-1 active:shadow-[2px_2px_0_var(--color-violet-300)] dark:bg-violet-500 dark:text-white dark:hover:bg-violet-500 sm:w-auto";
// On desktop the actions float over the dark end-screen overlay, hence white text.
const DECLINE = "h-auto min-h-11 whitespace-normal text-foreground font-medium sm:text-white/85 sm:hover:bg-white/15 sm:hover:text-white";
const ENTER = "animate-upsell-fade-in-up motion-reduce:animate-none";
// Staggered entrance once the overlay has faded in.
const delay = (index: number) => ({ animationDelay: `${250 + index * 150}ms` });

// The entrance animation owns `transform`, so it runs on a wrapper and the skew stays on the button.
type PrimaryProps = {
  index: number
  disabled: boolean
  onClick: () => void
  children: ReactNode
};

const Primary = ({
  index, disabled, onClick, children,
}: PrimaryProps) => (
  <span className={ENTER} style={delay(index)}>
    <Button className={PRIMARY} disabled={disabled} onClick={onClick}>
      <span className="flex skew-x-12 items-center gap-3 whitespace-normal">
        {children}
        <ChevronRight aria-hidden="true" className="size-5 transition-transform duration-150 ease-out group-hover:translate-x-1" />
      </span>
    </Button>
  </span>
);

export const ActivationActions = ({
  revealed, disabled, canSelfServe, onRespond,
}: Props) => (
  <div aria-live="polite" className="flex w-full flex-col items-center gap-4">
    {revealed && (
      <>
        <div className="flex w-full flex-col gap-4 px-2 sm:w-auto sm:flex-row sm:gap-6">
          {canSelfServe && <Primary index={0} disabled={disabled} onClick={() => onRespond("self_served")}>Je charge moi-même</Primary>}
          <Primary index={canSelfServe ? 1 : 0} disabled={disabled} onClick={() => onRespond("interested")}>Je veux être accompagné(e)</Primary>
        </div>
        <span className={ENTER} style={delay(canSelfServe ? 2 : 1)}>
          <Button variant="ghost" className={DECLINE} disabled={disabled} onClick={() => onRespond("declined")}>Pas intéressé(e)</Button>
        </span>
      </>
    )}
  </div>
);
